import { useContext, useRef } from 'react';
import { useFliptVariant } from '@flipt-io/flipt-client-react';

import type { FlagKey } from '../registry';

import type { BucketBy } from './FeatureFlagContext';
import { FeatureFlagContext } from './FeatureFlagContext';
import { shallowEqual } from './shallowEqual';

export interface UseFeatureVariantOptions {
  /** Bucketing mode: 'user' uses userId (cross-device consistent),
   *  'device' uses deviceId (stable across login/logout). */
  bucketBy: BucketBy;
  /** Optional attributes for Flipt targeting rules (e.g., { email: 'user@zep.us' }).
   *  Inline object literals are safe — the hook stabilizes the reference internally. */
  context?: Record<string, string>;
}

/**
 * Hook to evaluate a single variant feature flag.
 * Returns the selected variant key and whether the provider is ready.
 *
 * @param variantKey  - The variant flag key to evaluate
 * @param options  - bucketBy (required) + optional targeting context
 */
export function useFeatureVariant(
  variantKey: FlagKey,
  options: UseFeatureVariantOptions,
): {
  variant: string;
  isReady: boolean;
} {
  const { userEntityId, deviceEntityId, ssrVariants, isReady } =
    useContext(FeatureFlagContext);

  const effectiveEntityId =
    options.bucketBy === 'device' ? deviceEntityId : userEntityId;

  const stableRef = useRef(options.context);
  if (!shallowEqual(stableRef.current, options.context)) {
    stableRef.current = options.context;
  }

  const defaultValue = ssrVariants?.[variantKey] ?? '';
  const variant = useFliptVariant(
    variantKey,
    defaultValue,
    effectiveEntityId,
    stableRef.current,
  );

  return { variant, isReady };
}
